<template>
  <div class="w-100">
    <h3 class="title-2 m-0 mb-3">{{content.gallery_photos}}</h3>
    <div class="d-flex flex-column position-relative mb-5">
      <div
        v-if="isDisplayArrowsInMobile || isDisplayArrowsInDesktop"
        class="arrow left-arrow position-absolute rounded-circle p-1"
        @click="scrollThumbnail('left')"
      >
        <svg width="21" height="18" class="icon">
          <use xlink:href="#___icon___chevron-left-bold"></use>
        </svg>
      </div>
      <div
        class="d-flex overflow-hidden miniaturesContainer"
        ref="miniatureAccomodationContainer"
      >
        <div
          v-for="(prestation, index) in prestations"
          :key="`${index}-${prestation.accomodationName}`"
          class="d-flex align-items-center flex-column me-3 w-25"
        >
          <img
            :src="getImageUrl(prestation.picturesAccomodation[0])"
            alt="Miniature of Accommodation"
            class="prestation-image rounded"
            @click="$emit('navigate', prestation.accomodationName)"
          />
          <div class="subcaption prestation-name text-center w-100 mt-2">
            {{ prestation.accomodationName }}
          </div>
        </div>
      </div>
      <div
        v-if="isDisplayArrowsInMobile || isDisplayArrowsInDesktop"
        class="arrow right-arrow position-absolute rounded-circle p-1"
        @click="scrollThumbnail('right')"
      >
        <svg width="21" height="18" class="icon">
          <use xlink:href="#___icon___chevron-right-bold"></use>
        </svg>
      </div>
    </div>
  </div>
</template>

  <script setup lang="ts">
  // Vue
  import { ref, Ref } from "vue";

  // Utils
  import {isMobile} from '@/utils/utils.js'; 

  // Types
  import { IPicturesAccomodation, PrestationData } from "@/types/index";

  // Props
  const prestationsArray = defineProps<{ prestations: PrestationData[] }>();

  // Template REFs
  const miniatureAccomodationContainer: Ref<HTMLElement | null> = ref(null);

  // Content
  const content = window.content

  // Data
  const isDisplayArrowsInMobile = prestationsArray.prestations.length > 2 && isMobile()
  const isDisplayArrowsInDesktop = prestationsArray.prestations.length > 5 && !isMobile()

  /**
   * Retrieves the URL of the image to be displayed from the given IPicturesAccomodation object.
   *
   * @param {IPicturesAccomodation} image - The object containing different sizes of an accommodation picture.
   * @returns {string} - The URL of the image to be displayed.
   *
   * The function prioritizes the larger image sizes, returning the first available URL in the order:
   * 1. `imgXL`
   * 2. `imgLarge`
   * 3. `img`
   *
   * If the larger sizes are not available, it defaults to the next available image.
   */
  const getImageUrl = (image: IPicturesAccomodation): string => {
    return image.imgXL || image.imgLarge || image.img;
  };

  /**
   * Scrolls the thumbnail container left or right based on the direction provided.
   *
   * @param {('left' | 'right')} direction - The direction in which to scroll the container.
   *        Use 'left' to scroll towards the left and 'right' to scroll towards the right.
   *
   * The function calculates the scroll amount based on the container's width and scrolls
   * it smoothly in the specified direction. The amount of scroll is determined by dividing
   * the container's width by 4, allowing the user to scroll a quarter of the container's
   * width per click.
   */
  const scrollThumbnail = (direction: "left" | "right"): void => {
    const container = miniatureAccomodationContainer.value;
    if (container) {
      const scrollAmount = container.offsetWidth / 4;
      const scrollDirection = direction === "left" ? -scrollAmount : scrollAmount;
      container.scrollBy({ left: scrollDirection, behavior: "smooth" });
    }
  };
</script>

<style scoped>
/* ScrollBar section */
::-webkit-scrollbar {
  width: 1px !important; /* Adjust the width as needed */
}

::-webkit-scrollbar-track {
  background-color: #f1f1f1;
  border-radius: var(--border-radius-card); /* Adjust the border radius */
}

::-webkit-scrollbar-thumb {
  background-color: #888;
  border-radius: var(--border-radius-card); /* Adjust the border radius */
}
/* ScrollBar section */

.prestation-image {
  cursor: pointer;
  width: 125px;
  height: 125px;
  object-fit: cover;
}

.prestation-name {
  word-wrap: break-word;
  white-space: normal;
  text-wrap:balance;
}

.miniaturesContainer {
  white-space: nowrap;
  justify-content: space-evenly;
}

.arrow {
  background-color: rgb(237 230 230 / 81%);
  cursor: pointer;
  width: 35px;
  top: 30%;
}

.left-arrow {
  left: 5px;
}

.right-arrow {
  right: 5px;
}

@media (max-width: 780px) {
  .miniaturesContainer {
    overflow: auto !important;
    gap: 10%;
  }
}
</style>
